import { useCallback, useEffect, useState } from 'react';
import { api, type BugReportRow } from '../lib/api';
import { onBugReportUploaded, pendingBugReports } from '../lib/bugReports';

/**
 * After the training: the referee's open "Fehler merken" presses, each with
 * time / training / screen — one short description each (or "verwerfen").
 * The recording around each press is already stored; this adds the "what
 * was wrong" that only the referee knows.
 */
export function BugReportsScreen({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<BugReportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.myBugReports());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  // Presses queued while offline show up here as soon as they are uploaded.
  useEffect(() => onBugReportUploaded(() => void load()), [load]);

  const act = async (id: string, run: () => Promise<unknown>) => {
    setBusy(id);
    try {
      await run();
      setRows((cur) => cur?.filter((r) => r.id !== id) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const pending = pendingBugReports();
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Gemerkte Fehler beschreiben</h2>
        <button type="button" onClick={onBack} className="text-sm underline">
          Zurück
        </button>
      </div>
      <p className="text-sm text-zinc-400">
        Die Bildschirme vor, bei und nach dem Antippen sind schon gespeichert. Ein Satz, was nicht
        gestimmt hat, reicht.
      </p>
      {pending > 0 && (
        <p className="text-sm text-amber-300">
          {pending} gemerkte(r) Fehler noch nicht übertragen (offline) — erscheint hier, sobald
          wieder Verbindung besteht.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}{' '}
          <button type="button" onClick={() => void load()} className="underline">
            Erneut versuchen
          </button>
        </p>
      )}
      {rows === null && !error && <p role="status">Wird geladen …</p>}
      {rows?.length === 0 && <p className="text-sm text-zinc-400">Keine offenen Fehler. Danke!</p>}
      {rows?.map((r) => (
        <div key={r.id} className="space-y-2 rounded-lg border border-zinc-800 p-3">
          <p className="text-sm">
            <span className="font-semibold">
              {new Date(r.reportedAt).toLocaleString('de-AT', {
                dateStyle: 'short',
                timeStyle: 'medium',
              })}
            </span>{' '}
            · {r.sessionName}
            {r.screen ? ` · ${r.screen}` : ''}
            {r.sessionStatus === 'live' && (
              <span className="ml-2 text-xs text-amber-300">(Training läuft noch)</span>
            )}
          </p>
          <textarea
            value={drafts[r.id] ?? ''}
            onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
            placeholder="Was war falsch? z. B. „Nach Bestätigen sprang die Wertung zu Schütze 3 zurück“"
            rows={3}
            className="w-full rounded border border-zinc-700 bg-zinc-900 p-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy === r.id || !(drafts[r.id] ?? '').trim()}
              onClick={() =>
                void act(r.id, () => api.describeBugReport(r.id, (drafts[r.id] ?? '').trim()))
              }
              className="rounded bg-emerald-700 px-3 py-1 text-sm font-semibold disabled:opacity-50"
            >
              Speichern
            </button>
            <button
              type="button"
              disabled={busy === r.id}
              onClick={() => void act(r.id, () => api.dismissBugReport(r.id))}
              className="rounded border border-zinc-700 px-3 py-1 text-sm disabled:opacity-50"
            >
              Verwerfen (war kein Fehler)
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
